import { forwardRef, useCallback, useRef } from 'react';
import { FixedSizeList } from 'react-window';

// By default, we try to use the `id` property of each item as the key. If not
// present, we fall back on the index.
const defaultItemKeyFn = (index, data) => data[index]?.id ?? index;

/**
 * Given a list of `items` and a `rowRenderer` function, renders a virtualized list.
 *
 * `rowRenderer` example:
 * ```js
 * const rowRenderer = ({ index, data, style }) => (
 *   <div key={key} style={style}>
 *     {data[index]}
 *   </div>
 * );
 * ```
 *
 * Pass `ref` to get a reference to the inner `FixedSizeList` component.
 *
 */
export default forwardRef(function VirtualizedList(
  {
    height,
    width = '100%',
    items,
    rowRenderer,
    itemHeight = 20,
    itemKeyFn = defaultItemKeyFn,
  },
  ref,
) {
  return (
    <FixedSizeList
      ref={ref}
      width={width}
      height={height}
      itemData={items}
      itemCount={items.length}
      itemSize={itemHeight}
      itemKey={itemKeyFn}
    >
      {rowRenderer}
    </FixedSizeList>
  );
});

/**
 * Returns a ref for a `<VirtualizedList>` component and a `scrollTo` function to scroll to a specific index within it.
 *
 * Usage:
 * ```js
 * const ParentComponent = () => {
 *   const { listRef, scrollTo } = useVirtualizedScrollTo();
 *
 *   on('event', () => scrollTo(10));
 *
 *   return <VirtualizedList ref={listRef} />;
 * }
 * ```
 */
export const useVirtualizedScrollTo = () => {
  const listRef = useRef();
  const scrollTo = useCallback((index, align = 'smart') => {
    listRef.current.scrollToItem(index, align);
  }, []);

  return { listRef, scrollTo };
};
